import React, { useMemo } from "react";
import useSuburbPanelData from "../../../hooks/useSuburbPanelData";
import "./RightPanel.css";

const fmtNumber = (v) => (v == null || Number.isNaN(+v) ? "—" : `${+v}`);
const fmtWithUnit = (v, u) => {
  if (v == null || Number.isNaN(+v)) return "—";
  const val = +v;
  if (u && String(u).trim() === "%") return `${val}%`;
  if (u && String(u).toLowerCase().includes("ratio")) return `${(val * 100).toFixed(2)}%`;
  return u ? `${val} ${u}` : `${val}`;
};

const KPI_META = [
  { key: "vacancy_rate_sqm",     label: "Vacancy rate",       fmt: fmtWithUnit },
  { key: "demand_rea",           label: "Demand REA index",   fmt: (v) => fmtNumber(v) },
  { key: "houses_available",     label: "Houses available",    fmt: (v) => fmtNumber(v) },
  { key: "sales_last_12_months", label: "Sales (12m)",         fmt: (v) => fmtNumber(v) },
];

function makeGoogleEmbedUrl(lat, lon, suburb, city, zoom = 14) {
  if (typeof lat === "number" && typeof lon === "number") {
    const q = `${lat.toFixed(6)},${lon.toFixed(6)}`;
    return `https://www.google.com/maps?q=${encodeURIComponent(q)}&z=${zoom}&output=embed`;
  }
  const q = [suburb, city].filter(Boolean).join(", ");
  return q ? `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed` : null;
}

export default function RightPanel({ city, suburb, onClose }) {
  const INDICATORS = useMemo(() => KPI_META.map(k => k.key), []);
  const { loading, error, details, latest } = useSuburbPanelData(city, suburb, INDICATORS);

  const mapUrl = useMemo(
    () => makeGoogleEmbedUrl(details?.lat ?? null, details?.lon ?? null, suburb, city, 14),
    [details?.lat, details?.lon, suburb, city]
  );

  return (
    <aside className="rp">
      <div className="rp__header">
        <div className="rp__title">
          <div className="rp__suburb">{suburb || "—"}</div>
          <div className="rp__city">{city || "—"}</div>
        </div>
        <button className="rp__close" type="button" onClick={onClose} aria-label="Close panel">×</button>
      </div>

      <div className="rp__card">
        <div className="rp__label">Postcode</div>
        <div className="rp__value">{details?.postCode ?? "—"}</div>
      </div>

      <div className="rp__card">
        <div className="rp__label">Map</div>
        {mapUrl ? (
          <div className="rp__mapbox">
            <iframe
              title="map"
              className="rp__map"
              src={mapUrl}
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        ) : (
          <div className="rp__empty">Map unavailable</div>
        )}
      </div>

      <div className="rp__card">
        <div className="rp__label">Key indicators</div>
        <div className="rp__kpis">
          {KPI_META.map(({ key, label, fmt }) => {
            const item = latest?.[key] || null;
            const value = fmt(item?.value ?? null, item?.unit ?? null);
            const period = item?.period ?? null;
            return (
              <div key={key} className="rp__kpi">
                <div className="rp__kpi-label">{label}</div>
                <div className="rp__kpi-value">{value}</div>
                {period && <div className="rp__kpi-period">{String(period)}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {loading && <div className="rp__hint">Loading…</div>}
      {error && <div className="rp__error">Error loading suburb data</div>}
    </aside>
  );
}
